<template>
    <div class="proj-page">
        <!-- Header -->
        <div class="proj-header">
            <div class="proj-header-left">
                <div class="proj-icon-wrap"><i class="fas fa-project-diagram"></i></div>
                <h2 class="proj-title">Projects</h2>
                <span class="proj-count" v-if="projects.length">{{ projects.length }}</span>
            </div>
            <div class="proj-header-right">
                <div class="proj-filter-wrap">
                    <i class="fas fa-filter proj-filter-icon"></i>
                    <SelectDropdown
                        id="proj-company-filter"
                        v-if="companies.length"
                        :options="[{ id: '', title: 'All Companies' }, ...companies.map((item) => ({ id: item.id, title: item.name }))]"
                        :value="selectedCompanyId"
                        @change="selectedCompanyId = $event"
                    />
                </div>
                <AllowPermission :permission="PERMISSION_CODES.EDIT_PROJECTS">
                    <button class="proj-add-btn" @click="showForm = true">
                        <i class="fas fa-plus"></i>
                        New Project
                    </button>
                </AllowPermission>
            </div>
        </div>

        <!-- Form Modal -->
        <RightSideModal :show="showForm" :title="editMode ? 'Edit Project' : 'New Project'" @close="closeForm">
            <Form :fields="createFields" title="Project" submitUrl="/projects" updateRootUrl="/projects"
                @success="handleFormSuccess" @cancel="closeForm" :editMode="editMode" :editData="editData" />
        </RightSideModal>

        <!-- Loading -->
        <div v-if="loading" class="proj-loading"><div class="proj-spinner"></div><span>Loading projects...</span></div>

        <!-- Empty State -->
        <div v-else-if="!filteredProjects.length" class="proj-empty">
            <div class="proj-empty-icon"><i class="fas fa-project-diagram"></i></div>
            <h3>{{ selectedCompanyId ? 'No projects for this company' : 'No projects found' }}</h3>
            <p>Create your first project to get started</p>
        </div>

        <!-- Table -->
        <div v-else class="proj-table-wrap">
            <table class="proj-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Client</th>
                        <th>Measurement</th>
                        <th class="text-center">Start Date</th>
                        <th class="text-center">Target Date</th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="project in filteredProjects" :key="project.id">
                        <td class="proj-name-cell">
                            <div class="proj-name-badge"><i class="fas fa-folder-open"></i></div>
                            <span class="proj-name-text">{{ project.name }}</span>
                        </td>
                        <td>{{ project.company_name || '—' }}</td>
                        <td>{{ project.client_name || '—' }}</td>
                        <td>{{ project.measurementType || '—' }}</td>
                        <td class="text-center">{{ formatDate(project.startDate) }}</td>
                        <td class="text-center">{{ formatDate(project.endDate) }}</td>
                        <td class="proj-actions-cell">
                            <div class="proj-actions">
                                <AllowPermission :permission="PERMISSION_CODES.EDIT_PROJECTS">
                                    <button class="proj-action-btn proj-btn-edit" @click="enableEditing(project)" title="Edit">
                                        <i class="fas fa-pen"></i>
                                    </button>
                                </AllowPermission>
                                <AllowPermission :permission="PERMISSION_CODES.DELETE_PROJECTS">
                                    <button class="proj-action-btn proj-btn-delete" @click="deleteProject(project.id)" title="Delete">
                                        <i class="fas fa-trash-alt"></i>
                                    </button>
                                </AllowPermission>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import Form from "../../components/crud/form.vue"
import RightSideModal from "../../components/layout/RightSideModal.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import { createField } from "../../utils/common"
import { http } from "@/utils/http"
import { mapState } from "vuex"
import { PERMISSION_CODES } from "../../store/permission"

export default {
    data() {
        return {
            PERMISSION_CODES,
            loading: true,
            selectedCompanyId: "",
            createFields: [],
            showForm: false,
            editMode: false,
            editData: null,
            projects: [],
            clients: [],
        }
    },

    computed: {
        ...mapState("misc", ["companies", "projectMeasurements"]),
        filteredProjects() {
            if (!this.selectedCompanyId) return this.projects
            return this.projects.filter(p => {
                if (Array.isArray(p.companyIds)) return p.companyIds.includes(this.selectedCompanyId)
                return p.companyId === this.selectedCompanyId
            })
        }
    },
    components: { Form, RightSideModal, AllowPermission, SelectDropdown },
    async mounted() {
        await this.fetchClients()
        this.recreateFields()
        await this.fetchProjects()
        this.loading = false
    },
    methods: {
        async fetchClients() {
            try {
                const { data } = await http.get("/project-clients")
                this.clients = data.result || []
            } catch (err) {
                console.error("Error fetching clients:", err)
            }
        },
        recreateFields() {
            this.createFields = [
                createField("Name*", "name", "text", true),
                createField("Description*", "description", "text", true),
                createField(
                    "Select Company*",
                    "companyIds",
                    "multiselect",
                    true,
                    "",
                    this.companies.map((item) => ({
                        id: item.id,
                        title: `${item.name || ""}`,
                    }))
                ),
                createField(
                    "Client",
                    "clientId",
                    "select",
                    false,
                    "",
                    [{ id: "", title: "— No Client —" }, ...this.clients.map((c) => ({
                        id: c.id,
                        title: c.companyName ? `${c.name} (${c.companyName})` : c.name,
                    }))]
                ),
                createField(
                    "Measurement*",
                    "measurementType",
                    "select",
                    true,
                    "",
                    this.projectMeasurements.map((item) => ({
                        id: item,
                        title: `${item || ""}`,
                    }))
                ),
                createField("Measurement Amount*", "measurementTotalValue", "number", true),
                createField("Start Date*", "startDate", "date", true, new Date().toISOString().slice(0, 10)),
                createField("Target Date (must be after start date)*", "endDate", "date", true, ""),
            ]
        },
        closeForm() {
            this.showForm = false
            this.editMode = false
            this.editData = null
        },
        enableEditing(project) {
            this.editMode = true
            this.editData = { ...project }
            this.showForm = true
            window.scrollTo({ top: 0, behavior: "smooth" })
        },
        async handleFormSuccess() {
            this.closeForm()
            await this.fetchProjects()
        },
        async fetchProjects() {
            this.loading = true
            try {
                const res = await http.get("/projects")
                this.projects = res.data.result || []
            } catch (err) {
                console.log("Error fetching projects:", err)
                this.$toast?.error?.("Failed to load projects")
            }
            this.loading = false
        },
        async deleteProject(id) {
            if (!confirm("Delete this project?")) return
            try {
                await http.delete(`/projects/${id}`)
                this.$toast?.success?.("Project deleted successfully")
                this.projects = this.projects.filter(p => p.id !== id)
            } catch (err) {
                console.log("Error deleting project:", err)
                this.$toast?.error?.("Failed to delete project")
            }
        },
        formatDate(date) {
            if (!date) return "—"
            return new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.proj-page { animation: projFade .3s ease; }
@keyframes projFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }

/* Header */
.proj-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    padding: 12px 16px;
    margin-bottom: 16px;
    box-shadow: $shadowSm;
    flex-wrap: wrap;
}

.proj-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.proj-header-right {
    display: flex;
    align-items: center;
    gap: 10px;
}

.proj-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: linear-gradient(135deg, $primaryLight, darken($primaryLight, 4%));
    color: $primaryColor;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}

.proj-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}

.proj-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: $radiusFull;
    font-size: 11px;
    font-weight: 700;
    background: $primaryColor;
    color: white;
}

.proj-filter-wrap {
    position: relative;
    min-width: 180px;
}

.proj-filter-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    color: $gray400;
    pointer-events: none;
    z-index: 1;
}

.proj-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: none;
    border-radius: 6px;
    background: $primaryColor;
    color: white;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;

    &:hover {
        background: $primaryDark;
    }
}

/* Loading / Empty */
.proj-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 80px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    span {
        font-size: 13px;
        color: $gray400;
    }
}

.proj-spinner {
    width: 24px;
    height: 24px;
    border: 2.5px solid $gray200;
    border-top-color: $primaryColor;
    border-radius: 50%;
    animation: projSpin .7s linear infinite;
}

@keyframes projSpin {
    to { transform: rotate(360deg); }
}

.proj-empty {
    text-align: center;
    padding: 80px 16px;
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;

    h3 {
        font-size: 14px;
        font-weight: 700;
        color: $gray600;
        margin: 0 0 4px;
    }

    p {
        font-size: 12px;
        color: $gray400;
        margin: 0;
    }
}

.proj-empty-icon {
    width: 48px;
    height: 48px;
    border-radius: $radiusFull;
    background: $gray50;
    border: 1px solid $gray100;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;

    i {
        font-size: 20px;
        color: $gray300;
    }
}

/* Table */
.proj-table-wrap {
    background: white;
    border: 1px solid $gray100;
    border-radius: 8px;
    box-shadow: $shadowSm;
    overflow: hidden;
}

.proj-table {
    width: 100%;
    border-collapse: collapse;
}

.proj-table thead {
    background: $gray50;
    border-bottom: 2px solid $gray100;
}

.proj-table th {
    padding: 12px 14px;
    font-size: 11px;
    font-weight: 600;
    color: $gray500;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
    text-align: left;
}

.proj-table td {
    padding: 12px 14px;
    font-size: 13px;
    color: $gray700;
    font-weight: 500;
    border-bottom: 1px solid $gray100;
    vertical-align: middle;
}

.proj-table tbody tr {
    transition: background $transitionFast;

    &:last-child td {
        border-bottom: none;
    }

    &:hover {
        background: $gray50;
    }
}

.text-center {
    text-align: center;
}

/* Project Name Cell */
.proj-name-cell {
    display: flex;
    align-items: center;
    gap: 8px;
}

.proj-name-badge {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: linear-gradient(135deg, #eff6ff, #e0f2fe);
    color: #2563eb;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
}

.proj-name-text {
    font-weight: 600;
    color: $gray800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Actions */
.proj-actions-cell {
    text-align: center;
    padding: 12px 14px !important;
}

.proj-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    white-space: nowrap;
}

.proj-action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: 1px solid $gray200;
    border-radius: 6px;
    background: white;
    font-size: 11px;
    cursor: pointer;
    transition: all $transitionFast;
    font-family: inherit;
    flex-shrink: 0;

    i {
        font-size: 11px;
    }
}

.proj-btn-edit {
    color: $primaryColor;

    &:hover {
        background: $primaryLight;
        border-color: rgba(0, 94, 137, 0.15);
    }
}

.proj-btn-delete {
    color: $danger;

    &:hover {
        background: #fef2f2;
        border-color: rgba(220, 38, 38, 0.15);
    }
}

/* Responsive */
@media (max-width: 1200px) {
    .proj-table-wrap {
        overflow-x: auto;
    }
}

@media (max-width: 768px) {
    .proj-header {
        flex-direction: column;
        align-items: stretch;
    }

    .proj-header-right {
        justify-content: flex-end;
    }

    .proj-filter-wrap {
        min-width: 100%;
    }
}
</style>
